/* Paramètres › Intégrations (Scope v5 §3 Compte · batch 22 · Roadmap 0.4).

   What the account is connected to, one row each, status word + glyph:
   GTM / pixels · Google Business Profile · Google Wallet · Apple Wallet ·
   e-mail sender. Status only — the flows behind them are elsewhere (GTM's
   two uses are CR-13 in Clients; the GBP connect flow is the GBP_OAuth brief,
   Orchestrator + Technical). Replaces the /integrations → Clients redirect:
   the Orchestrator removes it in App.tsx (status doc).

   Paths and shapes are contract 01 §1.6 (GET /v1/app/integrations): Apple
   Wallet is the server's `appleWallet.state`, as Google Wallet is; the GBP
   date arrives as ISO and is written in French here (C-2). */
import { Link, useNavigate } from 'react-router-dom'
import { Button, Card, EmptyState, List, ListRow, PageHeader, Pill, Skeleton } from '@/ui'
import { useIntegrations } from './api'

/* v3.3 · WF-040 / WF-043 (one page): the reference's Printags / Gelato / Canva cards are the physical-orders track,
   DEFERRED (after 19/10) — no provider is drawn as live or « non connecté » here, one line says the phase. What is
   ported: « Retour aux supports », the door to the public destinations on the Google row, the sender block as a status
   (its setup screen is another batch's: no dead « Configurer l’expéditeur »). */

export default function Integrations() {
  const { data, isPending, isError, refetch } = useIntegrations()
  const nav = useNavigate()
  const apple = data?.appleWallet.state === 'on'
  const since = (iso?: string) => iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) : ''

  return (
    <section data-frame="IN-01" className="page" style={{ maxWidth: 820 }}>
      <PageHeader title="Intégrations"
        description="Préparez les services utilisés par vos supports. Une connexion qui tombe ne touche jamais le parcours client ni les cadeaux."
        actions={<Button size="sm" onClick={() => nav('/supports')}>Retour aux supports</Button>}
        next={<Link to="/parametres/compte">Compte et accès</Link>} />
      {isPending && <div style={{ display: 'grid', gap: 10 }}><Skeleton width="42%" /><Skeleton /><Skeleton width="65%" /></div>}
      {isError && (
        <EmptyState title="Impossible de charger vos intégrations."
          why="Votre connexion a peut-être sauté — rien n’a été perdu."
          action={<Button variant="primary" size="sm" onClick={() => refetch()}>Réessayer</Button>} />
      )}
      {data && (
        <List>
          <ListRow title="Google Business Profile"
            meta={data.gbp.state === 'on' ? `connecté le ${since(data.gbp.since)} · note et avis lus chaque jour`
              : data.gbp.state === 'expired' ? `connexion expirée le ${since(data.gbp.since)} · la note et les avis ne sont plus lus`
              : 'pour lire votre note et vos avis chaque jour — le lien vers votre formulaire d’avis Google, lui, vient de votre fiche'}
            end={<span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {data.gbp.state === 'on' && <Pill tone="ok">Connecté</Pill>}
              {data.gbp.state === 'expired' && <><Pill tone="crit">Expiré</Pill><Button size="sm" variant="primary">Reconnecter</Button></>}
              {data.gbp.state === 'off' && <Button size="sm" variant="primary">Connecter</Button>}
              {/* WF-040 · the public destinations (the review links) are Paramètres › Plateformes' */}
              <Button size="sm" variant="quiet" onClick={() => nav('/parametres/plateformes')}>Gérer vos plateformes</Button>
            </span>} />
          <ListRow title="Google Tag Manager · pixels"
            meta={data.gtm.on ? `conteneur ${data.gtm.detail} · après consentement seulement` : 'vos tags, après consentement — jamais sur Mes cadeaux ni à la caisse'}
            end={<span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {data.gtm.on ? <Pill tone="ok">Actif</Pill> : <Pill tone="warn">À configurer</Pill>}
              <Button size="sm" variant="quiet" onClick={() => nav('/clients?vue=gtm')}>{data.gtm.on ? 'Détail' : 'Configurer'}</Button>
            </span>} />
          <ListRow title="Google Wallet"
            meta={data.googleWallet.state === 'on' ? 'vos clients ajoutent leur cadeau à Google Wallet' : 'démo vérifiée — proposé à vos clients dès l’ouverture'}
            end={data.googleWallet.state === 'on' ? <Pill tone="ok">Actif</Pill> : <Pill tone="warn">Démo</Pill>} />
          <ListRow title="Apple Wallet"
            meta={apple ? 'vos clients ajoutent leur cadeau à Apple Wallet' : 'pas encore proposé à vos clients — il apparaîtra partout d’un coup, sans rien régler'}
            end={apple ? <Pill tone="ok">Actif</Pill> : <Pill tone="warn">Bientôt</Pill>} />
        </List>
      )}
      {data && (
        <Card data-testid="emails-salon">
          <p className="t-eyebrow" style={{ marginTop: 0 }}>E-mails du salon</p>
          <List>
            <ListRow title="Expéditeur des e-mails"
              meta={<span>{data.sender.from}{data.sender.verified ? ' · domaine vérifié' : ' · vérification du domaine en cours'}</span>}
              end={data.sender.verified ? <Pill tone="ok">Vérifié</Pill> : <Pill tone="warn">En cours</Pill>} />
          </List>
          <p className="f-help" style={{ margin: '10px 0 0' }}>Le nom, l’adresse d’envoi et l’adresse de réponse de vos e-mails. Les e-mails du parcours partent avec cet expéditeur.</p>
        </Card>
      )}
      <p className="f-help" data-testid="phase-ulterieure">Impression, cartes NFC et commande de matériel&nbsp;: phase ultérieure. Un téléchargement ne commande aucun matériel.</p>
      <p className="f-help">Exports, Google Sheets et automatisations&nbsp;: <Link to="/clients?vue=integrations">dans Clients</Link>.</p>
    </section>
  )
}
